/**
 * Ripia Authentication Service
 *
 * 認証ライフサイクル管理
 * @module auth/authenticationService
 */

import logger from '../../utils/logger';
import { AuthApiClient } from './shared/apiClient';
import { TokenStorage } from './shared/tokenStorage';
import { API_CONFIG, TOKEN_KEYS } from './constants';
import {
  AuthError,
  type User,
  type LoginRequest,
  type LoginResponse,
  type RefreshResponse,
  type EnhancedLoginRequest,
  type EnhancedLoginResponse,
  type RegisterWithEmailVerificationRequest,
  type RegisterWithEmailVerificationResponse,
  type VerifyEmailRequest,
  type VerifyEmailResponse,
  type ResendVerificationEmailRequest,
  type SecurityAuditInfo,
  type DeviceManagementInfo,
  type MFAEnableRequest,
  type MFAEnableResponse,
  type MFADisableRequest,
  type MFADisableResponse,
  type MFAVerifyRequest,
  type MFAVerifyResponse,
  type MFAStatusRequest,
  type MFAStatusResponse,
  type MFAGenerateBackupCodesRequest,
  type MFAGenerateBackupCodesResponse,
  type TempAuthToken,
  type JWTPayload,
  type EnhancedJWTPayload,
} from './types';
import {
  saveEncryptedToken,
  loadEncryptedToken,
  removeEncryptedToken,
  isTokenValid,
} from '../../utils/crypto';
import {
  EnhancedJWTService,
  type AuthenticationRequest,
} from '../../utils/enhancedJWT';
import {
  TokenBlacklistService,
  type RevocationRequest,
} from '../../utils/tokenBlacklist';
import {
  DeviceAuthService,
  type DeviceAuthResult,
} from '../../utils/deviceAuth';
import { AuditLogService, AuditEventType } from '../auditLogService';
import MFAService from '../mfaService';

/**
 * Authentication Service
 * 認証ライフサイクル・ログイン・ログアウト・トークン管理
 */
export class AuthenticationService {
  // ========================
  // Login Methods
  // ========================

  /**
   * ログイン（メール・パスワード）- エンタープライズセキュリティ統合
   */
  static async login(credentials: LoginRequest): Promise<LoginResponse> {
    return this.loginBasic(credentials);
  }

  /**
   * エンタープライズレベルセキュアログイン
   */
  static async loginSecure(
    credentials: EnhancedLoginRequest
  ): Promise<EnhancedLoginResponse> {
    try {
      // 1. デバイス特性収集
      const deviceCharacteristics =
        credentials.deviceCharacteristics ||
        DeviceAuthService.collectDeviceCharacteristics();
      const deviceFingerprint = DeviceAuthService.generateAdvancedFingerprint(
        deviceCharacteristics
      );

      // 2. 基本認証実行
      const basicLogin = await this.loginBasic(credentials);

      // 3. エンタープライズJWT認証リクエスト構築
      const authRequest: AuthenticationRequest = {
        userId: basicLogin.user.id,
        email: basicLogin.user.email,
        role: basicLogin.user.role,
        permissions: basicLogin.user.permissions || [],
        tenantId: (basicLogin.user as User & { tenantId?: string }).tenantId,
        deviceFingerprint,
        securityLevel: credentials.securityLevel || 'standard',
        additionalClaims: {
          storeId: basicLogin.user.storeId,
        },
      };

      // 4. エンタープライズJWT生成
      const enhancedAuth = await EnhancedJWTService.authenticate(authRequest);

      if (!enhancedAuth.success) {
        throw new AuthError(
          'エンタープライズ認証に失敗しました',
          'SERVER_ERROR'
        );
      }

      // 5. デバイス認証実行
      const deviceAuth = await DeviceAuthService.authenticateDevice(
        basicLogin.user.id,
        deviceCharacteristics
      );

      // 6. セキュリティレベル決定
      const securityLevel = this.determineSecurityLevel(
        credentials.securityLevel,
        deviceAuth
      );

      // 7. エンタープライズトークン保存
      this.saveEnhancedTokens({
        accessToken: enhancedAuth.tokens.accessToken,
        refreshToken: enhancedAuth.tokens.refreshToken,
        user: basicLogin.user,
        jti: enhancedAuth.jti,
        deviceFingerprint,
        securityLevel,
      });

      return {
        ...basicLogin,
        accessToken: enhancedAuth.tokens.accessToken,
        refreshToken: enhancedAuth.tokens.refreshToken,
        jti: enhancedAuth.jti,
        deviceFingerprint,
        securityLevel,
        isDeviceTrusted: deviceAuth.trustScore > 80,
      };
    } catch (error) {
      logger.error('エンタープライズログインエラー:', error);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError('セキュアログインに失敗しました', 'NETWORK_ERROR');
    }
  }

  /**
   * 基本ログイン（既存機能・完全保持）
   */
  private static async loginBasic(
    credentials: LoginRequest
  ): Promise<LoginResponse> {
    try {
      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        const response = await AuthApiClient.post<LoginResponse>(
          API_CONFIG.ENDPOINTS.LOGIN,
          credentials
        );
        this.saveTokens(response);
        logger.log('✅ ログイン成功 - トークン保存完了:', response.user.email);
        return response;
      }

      // 開発環境用モック実装
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          try {
            if (!credentials.email || !credentials.password) {
              reject(
                new AuthError(
                  'メールアドレスとパスワードを入力してください',
                  'VALIDATION_ERROR'
                )
              );
              return;
            }

            if (credentials.password.length < 8) {
              reject(
                new AuthError(
                  'パスワードは8文字以上で入力してください',
                  'VALIDATION_ERROR'
                )
              );
              return;
            }

            const userRole: 'owner' | 'staff' =
              credentials.email === 'test0513@ripia.jp' ||
              credentials.email === 'test0701@ripia.jp' ||
              credentials.email.includes('owner')
                ? 'owner'
                : 'staff';

            const mockUser: User = {
              id: `user-${Date.now()}`,
              email: credentials.email,
              name: credentials.email.split('@')[0],
              role: userRole,
              storeId: '',
              permissions:
                userRole === 'owner'
                  ? ['*']
                  : [
                      'read:customers',
                      'write:customers',
                      'read:reservations',
                      'write:reservations',
                    ],
              createdAt: new Date().toISOString(),
              lastLoginAt: new Date().toISOString(),
            };

            const mockResponse: LoginResponse = {
              user: mockUser,
              accessToken: this.generateMockJWT(mockUser),
              refreshToken: `refresh_${Date.now()}_${Math.random().toString(36)}`,
              expiresIn: 24 * 60 * 60,
            };

            AuditLogService.logLoginSuccess(
              mockUser.id,
              mockUser.email,
              'email'
            );
            resolve(mockResponse);
          } catch (error) {
            logger.error('❌ [AuthService] ログイン処理エラー:', error);
            reject(new AuthError('ログインに失敗しました', 'SERVER_ERROR'));
          }
        }, 1500);
      });
    } catch (error) {
      logger.error('ログインエラー:', error);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError('ログインに失敗しました', 'NETWORK_ERROR');
    }
  }

  // ========================
  // Logout Methods
  // ========================

  /**
   * ログアウト（エンタープライズセキュリティ統合）
   */
  static async logout(): Promise<void> {
    try {
      const currentUser = await this.getCurrentUser();
      await this.logoutSecure();

      if (currentUser) {
        await AuditLogService.log(AuditEventType.LOGOUT, {
          userId: currentUser.id,
          userEmail: currentUser.email,
          success: true,
        });
      }
    } catch (error) {
      logger.error('ログアウトエラー:', error);
      this.clearAllTokens();
    }
  }

  /**
   * エンタープライズセキュアログアウト
   */
  static async logoutSecure(): Promise<void> {
    try {
      const refreshToken = loadEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
      const jti = loadEncryptedToken('ripia_jwt_jti');
      const currentUser = await this.getCurrentUser();

      // 1. トークンブラックリスト登録
      if (jti && currentUser) {
        const revocationRequest: RevocationRequest = {
          jti,
          userId: currentUser.id,
          reason: 'logout',
        };
        await TokenBlacklistService.revokeToken(revocationRequest);
      }

      // 2. サーバーサイドログアウト
      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        if (refreshToken) {
          await AuthApiClient.post(API_CONFIG.ENDPOINTS.LOGOUT, {
            refreshToken,
          });
        }
      }

      // 3. 全ローカルトークン削除
      this.clearAllTokens();

      logger.info('セキュアログアウトが完了しました');
    } catch (error) {
      logger.error('セキュアログアウトエラー:', error);
      this.clearAllTokens();
    }
  }

  // ========================
  // Token Refresh Methods
  // ========================

  /**
   * トークン更新 - エンタープライズセキュリティ統合
   */
  static async refreshToken(): Promise<RefreshResponse> {
    return this.refreshTokenBasic();
  }

  /**
   * エンタープライズトークン更新
   */
  static async refreshTokenSecure(): Promise<
    RefreshResponse & { jti: string; deviceFingerprint?: string }
  > {
    try {
      const refreshToken = loadEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        throw new AuthError(
          'リフレッシュトークンが見つかりません',
          'TOKEN_EXPIRED'
        );
      }

      // デバイスフィンガープリント検証
      const storedFingerprint = loadEncryptedToken('ripia_device_fingerprint');
      const currentCharacteristics =
        DeviceAuthService.collectDeviceCharacteristics();
      const currentFingerprint = DeviceAuthService.generateAdvancedFingerprint(
        currentCharacteristics
      );

      if (storedFingerprint && storedFingerprint !== currentFingerprint) {
        const trustScore = await DeviceAuthService.authenticateDevice(
          'current-user',
          currentCharacteristics
        );
        if (trustScore.trustScore < 50) {
          throw new AuthError('デバイス認証に失敗しました', 'UNAUTHORIZED');
        }
      }

      // エンタープライズJWTローテーション実行
      const rotationResult =
        await EnhancedJWTService.refreshToken(refreshToken);

      if (!rotationResult.success) {
        throw new AuthError('トークンの更新に失敗しました', 'TOKEN_EXPIRED');
      }

      // 新しいトークン保存
      TokenStorage.saveNewAccessToken(rotationResult.tokens.accessToken);
      if (rotationResult.tokens.refreshToken) {
        saveEncryptedToken(
          rotationResult.tokens.refreshToken,
          TOKEN_KEYS.REFRESH_TOKEN
        );
      }

      return {
        accessToken: rotationResult.tokens.accessToken,
        expiresIn: 24 * 60 * 60,
        jti: rotationResult.jti,
        deviceFingerprint: currentFingerprint,
      };
    } catch (error) {
      logger.error('エンタープライズトークン更新エラー:', error);
      if (error instanceof AuthError) {
        throw error;
      }
      throw new AuthError(
        'セキュアトークン更新に失敗しました',
        'TOKEN_EXPIRED'
      );
    }
  }

  /**
   * 基本トークン更新（既存機能・完全保持）
   */
  private static async refreshTokenBasic(): Promise<RefreshResponse> {
    try {
      const refreshToken = loadEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
      if (!refreshToken) {
        throw new AuthError(
          'リフレッシュトークンが見つかりません',
          'TOKEN_EXPIRED'
        );
      }

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        const response = await AuthApiClient.post<RefreshResponse>(
          API_CONFIG.ENDPOINTS.REFRESH,
          {
            refreshToken,
          }
        );

        saveEncryptedToken(response.accessToken, TOKEN_KEYS.ACCESS_TOKEN);
        logger.log('✅ AccessToken更新完了');

        return response;
      }

      // モック実装
      return new Promise((resolve) => {
        setTimeout(() => {
          const userData = loadEncryptedToken(TOKEN_KEYS.USER_DATA);
          const user = userData ? JSON.parse(userData) : null;

          resolve({
            accessToken: this.generateMockJWT(user || { id: 'mock-user' }),
            expiresIn: 24 * 60 * 60,
          });
        }, 500);
      });
    } catch (error) {
      logger.error('トークン更新エラー:', error);
      throw new AuthError('トークンの更新に失敗しました', 'TOKEN_EXPIRED');
    }
  }

  // ========================
  // Authentication Status
  // ========================

  /**
   * 認証状態チェック - エンタープライズセキュリティ統合
   */
  static isAuthenticated(): boolean {
    return this.isAuthenticatedBasic();
  }

  /**
   * エンタープライズセキュア認証状態チェック
   */
  static async isAuthenticatedSecure(): Promise<boolean> {
    try {
      const token = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
      if (!token) return false;

      // 1. 基本トークン有効性チェック
      if (!isTokenValid(token)) {
        return false;
      }

      // 2. ブラックリストチェック
      const jti = loadEncryptedToken('ripia_jwt_jti');
      if (jti) {
        const isRevoked = await TokenBlacklistService.isTokenRevoked(jti);
        if (isRevoked) {
          logger.warn('無効化されたトークンが検出されました');
          this.clearAllTokens();
          return false;
        }
      }

      // 3. デバイス認証チェック
      const storedFingerprint = loadEncryptedToken('ripia_device_fingerprint');
      const securityLevel =
        loadEncryptedToken('ripia_security_level') || 'standard';

      if (securityLevel === 'premium' && storedFingerprint) {
        const currentCharacteristics =
          DeviceAuthService.collectDeviceCharacteristics();
        const currentFingerprint =
          DeviceAuthService.generateAdvancedFingerprint(currentCharacteristics);

        if (storedFingerprint !== currentFingerprint) {
          logger.warn('デバイスフィンガープリント不整合を検出');
          return false;
        }
      }

      return true;
    } catch (error) {
      logger.error('セキュア認証チェックエラー:', error);
      return false;
    }
  }

  /**
   * 基本認証状態チェック（既存機能・完全保持）
   */
  private static isAuthenticatedBasic(): boolean {
    const token = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    return token ? isTokenValid(token) : false;
  }

  // ========================
  // User Methods
  // ========================

  /**
   * ユーザー情報取得
   */
  static async getCurrentUser(): Promise<User | null> {
    try {
      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        return await AuthApiClient.get<User>(API_CONFIG.ENDPOINTS.ME);
      }

      const userData = loadEncryptedToken(TOKEN_KEYS.USER_DATA);
      if (!userData) {
        // 旧形式のキーもチェック（後方互換性）
        const oldUserData = localStorage.getItem('userData');
        if (oldUserData) {
          try {
            const user = JSON.parse(oldUserData);
            localStorage.setItem(TOKEN_KEYS.USER_DATA, JSON.stringify(user));
            localStorage.removeItem('userData');
            return user;
          } catch (e) {
            logger.error('旧形式ユーザーデータのパースエラー:', e);
          }
        }
        return null;
      }

      return JSON.parse(userData);
    } catch (error) {
      logger.error('ユーザー情報取得エラー:', error);
      return null;
    }
  }

  // ========================
  // Email Verification
  // ========================

  /**
   * メール認証付き新規登録
   */
  static async registerWithEmailVerification(
    request: RegisterWithEmailVerificationRequest
  ): Promise<RegisterWithEmailVerificationResponse> {
    try {
      logger.log('📝 メール認証付き新規登録開始:', request.email);

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        return await AuthApiClient.post<RegisterWithEmailVerificationResponse>(
          API_CONFIG.ENDPOINTS.REGISTER_WITH_EMAIL_VERIFICATION,
          request
        );
      }

      // モック実装
      const mockUserId = `user_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
      await new Promise((resolve) => setTimeout(resolve, 2000));

      const mockVerificationCode = Math.floor(
        100000 + Math.random() * 900000
      ).toString();
      logger.log('🔐 [MOCK] 認証コード生成:', mockVerificationCode);

      localStorage.setItem(
        `verification_${mockUserId}`,
        JSON.stringify({
          code: mockVerificationCode,
          email: request.email,
          userData: request,
          expires: Date.now() + 15 * 60 * 1000,
        })
      );

      return {
        success: true,
        message: 'メール認証コードを送信しました。メールをご確認ください。',
        userId: mockUserId,
        verificationRequired: true,
      };
    } catch (error) {
      logger.error('❌ メール認証付き新規登録エラー:', error);

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError(
        '新規登録に失敗しました。別のメールアドレスをお試しください。',
        'REGISTRATION_FAILED'
      );
    }
  }

  /**
   * メール認証の実行
   */
  static async verifyEmail(
    request: VerifyEmailRequest
  ): Promise<VerifyEmailResponse> {
    try {
      logger.log('🔐 メール認証開始:', request.userId);

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        const response = (await AuthApiClient.post(
          API_CONFIG.ENDPOINTS.VERIFY_EMAIL,
          request
        )) as VerifyEmailResponse;

        if (response.accessToken && response.refreshToken && response.user) {
          const loginResponse: LoginResponse = {
            user: response.user as User,
            accessToken: response.accessToken,
            refreshToken: response.refreshToken,
            expiresIn: 3600,
          };
          this.saveTokens(loginResponse);
          logger.log(
            '✅ メール認証成功 - トークン保存完了:',
            response.user.email
          );
        }

        return response;
      }

      // モック実装
      const verificationData = localStorage.getItem(
        `verification_${request.userId}`
      );

      if (!verificationData) {
        throw new AuthError(
          '認証情報が見つかりません。もう一度新規登録を行ってください。',
          'VERIFICATION_NOT_FOUND'
        );
      }

      const parsedData = JSON.parse(verificationData);

      if (Date.now() > parsedData.expires) {
        localStorage.removeItem(`verification_${request.userId}`);
        throw new AuthError(
          '認証コードの有効期限が切れています。もう一度新規登録を行ってください。',
          'VERIFICATION_EXPIRED'
        );
      }

      if (parsedData.code !== request.verificationCode) {
        throw new AuthError(
          '認証コードが正しくありません。',
          'INVALID_VERIFICATION_CODE'
        );
      }

      await new Promise((resolve) => setTimeout(resolve, 1000));
      localStorage.removeItem(`verification_${request.userId}`);

      const userData = parsedData.userData;
      const mockUser: User = {
        id: request.userId,
        email: userData.email,
        name: `${userData.firstName} ${userData.lastName}`,
        role: 'owner',
        createdAt: new Date().toISOString(),
      };

      const mockLoginResponse: LoginResponse = {
        user: mockUser,
        accessToken: `mock_jwt_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        refreshToken: `refresh_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`,
        expiresIn: 3600,
      };

      this.saveTokens(mockLoginResponse);

      return {
        success: true,
        message: 'メール認証が完了しました。',
        shouldRedirectToOrganizationCreation: true,
      };
    } catch (error) {
      logger.error('❌ メール認証エラー:', error);

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('認証に失敗しました。', 'VERIFICATION_FAILED');
    }
  }

  /**
   * 認証メール再送信
   */
  static async resendVerificationEmail(
    request: ResendVerificationEmailRequest
  ): Promise<void> {
    try {
      logger.log('📧 認証メール再送信開始:', request.userId);

      if (import.meta.env?.VITE_USE_REAL_API === 'true') {
        await AuthApiClient.post(
          API_CONFIG.ENDPOINTS.RESEND_VERIFICATION_EMAIL,
          request
        );
        return;
      }

      // モック実装
      const verificationData = localStorage.getItem(
        `verification_${request.userId}`
      );

      if (!verificationData) {
        throw new AuthError(
          '認証情報が見つかりません。もう一度新規登録を行ってください。',
          'VERIFICATION_NOT_FOUND'
        );
      }

      const parsedData = JSON.parse(verificationData);
      const newVerificationCode = Math.floor(
        100000 + Math.random() * 900000
      ).toString();
      logger.log('🔐 [MOCK] 新しい認証コード:', newVerificationCode);

      parsedData.code = newVerificationCode;
      parsedData.expires = Date.now() + 15 * 60 * 1000;

      localStorage.setItem(
        `verification_${request.userId}`,
        JSON.stringify(parsedData)
      );
      await new Promise((resolve) => setTimeout(resolve, 2000));

      logger.log('✅ 認証メール再送信完了');
    } catch (error) {
      logger.error('❌ 認証メール再送信エラー:', error);

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError(
        '認証メールの再送信に失敗しました。',
        'RESEND_FAILED'
      );
    }
  }

  // ========================
  // Session Management
  // ========================

  /**
   * アクティブセッション一覧取得
   */
  static async getActiveSessions(): Promise<SecurityAuditInfo[]> {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      // TODO: 実装時はAPIからセッション一覧を取得
      return [
        {
          tokenId: loadEncryptedToken('ripia_jwt_jti') || 'current-session',
          issuedAt: Date.now() - 2 * 60 * 60 * 1000,
          expiresAt: Date.now() + 22 * 60 * 60 * 1000,
          deviceFingerprint:
            loadEncryptedToken('ripia_device_fingerprint') || undefined,
          lastActivity: Date.now() - 5 * 60 * 1000,
          securityLevel:
            loadEncryptedToken('ripia_security_level') || 'standard',
        },
      ];
    } catch (error) {
      logger.error('アクティブセッション取得エラー:', error);
      return [];
    }
  }

  /**
   * 特定セッション無効化
   */
  static async revokeSession(tokenId: string): Promise<boolean> {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      const revocationRequest: RevocationRequest = {
        jti: tokenId,
        userId: currentUser.id,
        reason: 'admin',
      };

      return await TokenBlacklistService.revokeToken(revocationRequest);
    } catch (error) {
      logger.error('セッション無効化エラー:', error);
      return false;
    }
  }

  /**
   * 全セッション無効化（現在のセッション以外）
   */
  static async revokeAllOtherSessions(): Promise<number> {
    try {
      const currentUser = await this.getCurrentUser();
      if (!currentUser) {
        throw new AuthError('認証が必要です', 'UNAUTHORIZED');
      }

      const currentJti = loadEncryptedToken('ripia_jwt_jti');
      const revokedCount = await TokenBlacklistService.revokeAllUserTokens(
        currentUser.id,
        'security'
      );

      if (currentJti) {
        logger.info(`現在のセッション ${currentJti} は保持されました`);
      }

      return revokedCount;
    } catch (error) {
      logger.error('他セッション無効化エラー:', error);
      return 0;
    }
  }

  // ========================
  // MFA Methods
  // ========================

  /**
   * MFA有効化
   */
  static async enableMFA(
    request: MFAEnableRequest
  ): Promise<MFAEnableResponse> {
    try {
      logger.log('🔐 [AuthService] MFA有効化開始:', { userId: request.userId });

      if (!request.userId || !request.secretKey || !request.verificationCode) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      if (!/^\d{6}$/.test(request.verificationCode)) {
        throw new AuthError(
          '認証コードは6桁の数字で入力してください',
          'INVALID_VERIFICATION_CODE'
        );
      }

      const isValidCode = MFAService.validateMFASetup(
        request.secretKey,
        request.verificationCode
      );
      if (!isValidCode) {
        throw new AuthError(
          '認証コードが正しくありません',
          'INVALID_VERIFICATION_CODE'
        );
      }

      const mockResponse: MFAEnableResponse = {
        success: true,
        message: 'MFAが正常に有効化されました',
        backupCodesCount: request.backupCodes.length,
      };

      logger.log('✅ [AuthService] MFA有効化完了:', {
        userId: request.userId,
        backupCodesCount: mockResponse.backupCodesCount,
      });

      return mockResponse;
    } catch (error) {
      logger.error('❌ [AuthService] MFA有効化エラー:', {
        error,
        userId: request.userId,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('MFAの有効化に失敗しました', 'MFA_ENABLE_FAILED');
    }
  }

  /**
   * MFA無効化
   */
  static async disableMFA(
    request: MFADisableRequest
  ): Promise<MFADisableResponse> {
    try {
      logger.log('🔐 [AuthService] MFA無効化開始:', { userId: request.userId });

      if (!request.userId || !request.currentPassword) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      if (!request.verificationCode && !request.backupCode) {
        throw new AuthError(
          '認証コードまたはバックアップコードが必要です',
          'VALIDATION_ERROR'
        );
      }

      const mockResponse: MFADisableResponse = {
        success: true,
        message: 'MFAが正常に無効化されました',
      };

      logger.log('✅ [AuthService] MFA無効化完了:', { userId: request.userId });

      return mockResponse;
    } catch (error) {
      logger.error('❌ [AuthService] MFA無効化エラー:', {
        error,
        userId: request.userId,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('MFAの無効化に失敗しました', 'MFA_DISABLE_FAILED');
    }
  }

  /**
   * MFA認証
   */
  static async verifyMFA(
    request: MFAVerifyRequest
  ): Promise<MFAVerifyResponse> {
    try {
      logger.log('🔐 [AuthService] MFA認証開始:', {
        isBackupCode: request.isBackupCode,
        codeLength: request.code?.length,
      });

      if (!request.tempToken || !request.code) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      let tempTokenData: TempAuthToken;
      try {
        tempTokenData = {
          tempToken: request.tempToken,
          expiresAt: Date.now() + 300000,
          email: 'user@example.com',
          userId: 'user123',
          mfaRequired: true,
        };
      } catch {
        throw new AuthError(
          '認証セッションの有効期限が切れました',
          'TEMP_TOKEN_EXPIRED'
        );
      }

      let isValidCode = false;
      let backupCodesRemaining: number | undefined;

      if (request.isBackupCode) {
        isValidCode = /^[A-F0-9]{4}-[A-F0-9]{4}$/i.test(request.code);
        backupCodesRemaining = isValidCode ? 8 : undefined;
      } else {
        isValidCode = /^\d{6}$/.test(request.code);
      }

      if (!isValidCode) {
        throw new AuthError(
          request.isBackupCode
            ? 'バックアップコードが正しくありません'
            : '認証コードが正しくありません',
          'INVALID_MFA_CODE'
        );
      }

      const jwtPayload: JWTPayload = {
        sub: tempTokenData.userId,
        email: tempTokenData.email,
        role: 'owner',
        permissions: ['all'],
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
        mfa_enabled: true,
        mfa_verified: true,
        security_level: 'high',
      };

      const accessToken = this.generateMockJWT(jwtPayload);
      const refreshToken = `refresh_${Date.now()}_${Math.random().toString(36)}`;

      const user: User = {
        id: tempTokenData.userId,
        email: tempTokenData.email,
        name: 'Mock User',
        role: 'owner',
        emailVerified: true,
        mfaEnabled: true,
        createdAt: new Date().toISOString(),
      };

      const response: MFAVerifyResponse = {
        success: true,
        message: 'MFA認証が完了しました',
        accessToken,
        refreshToken,
        user,
        backupCodesRemaining,
      };

      logger.log('✅ [AuthService] MFA認証完了:', {
        userId: tempTokenData.userId,
        backupCodesRemaining,
      });

      return response;
    } catch (error) {
      logger.error('❌ [AuthService] MFA認証エラー:', {
        error,
        isBackupCode: request.isBackupCode,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError('MFA認証に失敗しました', 'MFA_VERIFICATION_FAILED');
    }
  }

  /**
   * MFA状態取得
   */
  static async getMFAStatus(
    request: MFAStatusRequest
  ): Promise<MFAStatusResponse> {
    try {
      logger.log('🔐 [AuthService] MFA状態取得開始:', {
        userId: request.userId,
      });

      const mockResponse: MFAStatusResponse = {
        isEnabled: false,
        backupCodesRemaining: 0,
        lastUsed: undefined,
        setupDate: undefined,
      };

      logger.log('✅ [AuthService] MFA状態取得完了:', {
        userId: request.userId,
        isEnabled: mockResponse.isEnabled,
      });

      return mockResponse;
    } catch (error) {
      logger.error('❌ [AuthService] MFA状態取得エラー:', {
        error,
        userId: request.userId,
      });
      throw new AuthError('MFA状態の取得に失敗しました', 'MFA_STATUS_FAILED');
    }
  }

  /**
   * バックアップコード再生成
   */
  static async generateNewBackupCodes(
    request: MFAGenerateBackupCodesRequest
  ): Promise<MFAGenerateBackupCodesResponse> {
    try {
      logger.log('🔐 [AuthService] バックアップコード再生成開始:', {
        userId: request.userId,
      });

      if (!request.userId || !request.currentPassword) {
        throw new AuthError(
          '必須パラメータが不足しています',
          'VALIDATION_ERROR'
        );
      }

      if (!request.verificationCode && !request.backupCode) {
        throw new AuthError(
          '認証コードまたはバックアップコードが必要です',
          'VALIDATION_ERROR'
        );
      }

      const newBackupCodes = MFAService.generateBackupCodes();

      return {
        success: true,
        message: '新しいバックアップコードが生成されました',
        backupCodes: newBackupCodes,
      };
    } catch (error) {
      logger.error('❌ [AuthService] バックアップコード再生成エラー:', {
        error,
        userId: request.userId,
      });

      if (error instanceof AuthError) {
        throw error;
      }

      throw new AuthError(
        'バックアップコードの再生成に失敗しました',
        'BACKUP_CODES_GENERATION_FAILED'
      );
    }
  }

  // ========================
  // Security Methods
  // ========================

  /**
   * セキュリティステータス取得
   */
  static async getSecurityStatus(): Promise<{
    securityLevel: 'standard' | 'high' | 'premium';
    mfaRequired: boolean;
    passwordChangeRequired: boolean;
    accountLocked: boolean;
    suspiciousActivity: boolean;
    lastLogin?: string;
    lastPasswordChange?: string;
  }> {
    // TODO: V2 APIからセキュリティステータスを取得
    return {
      securityLevel:
        (loadEncryptedToken('ripia_security_level') as
          | 'standard'
          | 'high'
          | 'premium') || 'standard',
      mfaRequired: false,
      passwordChangeRequired: false,
      accountLocked: false,
      suspiciousActivity: false,
      lastLogin: new Date().toISOString(),
      lastPasswordChange: undefined,
    };
  }

  /**
   * パスワードポリシー取得
   */
  static async getPasswordPolicy(): Promise<{
    minLength: number;
    requireUppercase: boolean;
    requireLowercase: boolean;
    requireNumbers: boolean;
    requireSpecialChars: boolean;
    maxAge: number;
    preventReuse: number;
  }> {
    // TODO: V2 APIからパスワードポリシーを取得
    return {
      minLength: 8,
      requireUppercase: true,
      requireLowercase: true,
      requireNumbers: true,
      requireSpecialChars: false,
      maxAge: 90,
      preventReuse: 5,
    };
  }

  /**
   * デバイス管理情報取得
   */
  static async getDeviceInfo(): Promise<DeviceManagementInfo | null> {
    try {
      const deviceFingerprint = loadEncryptedToken('ripia_device_fingerprint');
      if (!deviceFingerprint) return null;

      const trustScore = parseInt(
        loadEncryptedToken('ripia_device_trust_score') || '50',
        10
      );
      const lastUsed = parseInt(
        loadEncryptedToken('ripia_last_activity') || Date.now().toString(),
        10
      );

      return {
        fingerprint: deviceFingerprint,
        trustScore,
        lastUsed,
        isBlocked: false,
        deviceName: 'Current Device',
      };
    } catch (error) {
      logger.error('デバイス情報取得エラー:', error);
      return null;
    }
  }

  // ========================
  // Token Management
  // ========================

  /**
   * トークン保存
   */
  static saveTokens(response: LoginResponse, rememberMe?: boolean): void {
    const storageType = rememberMe ? 'localStorage' : 'sessionStorage';

    saveEncryptedToken(
      response.accessToken,
      TOKEN_KEYS.ACCESS_TOKEN,
      storageType
    );
    saveEncryptedToken(
      response.refreshToken,
      TOKEN_KEYS.REFRESH_TOKEN,
      storageType
    );
    saveEncryptedToken(
      JSON.stringify(response.user),
      TOKEN_KEYS.USER_DATA,
      storageType
    );

    if (rememberMe !== undefined) {
      localStorage.setItem('ripia_remember_me', rememberMe.toString());
    }
  }

  /**
   * 新しいアクセストークン保存
   */
  static saveNewAccessToken(accessToken: string): void {
    saveEncryptedToken(accessToken, TOKEN_KEYS.ACCESS_TOKEN);
  }

  /**
   * トークンクリア
   */
  static clearTokens(): void {
    removeEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
    removeEncryptedToken(TOKEN_KEYS.REFRESH_TOKEN);
    removeEncryptedToken(TOKEN_KEYS.USER_DATA);
  }

  /**
   * 全トークンクリア（エンタープライズセキュリティ統合）
   */
  static clearAllTokens(): void {
    try {
      this.clearTokens();
      TokenStorage.clearMultiTenantTokens();

      // エンタープライズセキュリティトークンクリア
      removeEncryptedToken('ripia_jwt_jti');
      removeEncryptedToken('ripia_device_fingerprint');
      removeEncryptedToken('ripia_security_level');
      removeEncryptedToken('ripia_session_id');
      removeEncryptedToken('ripia_device_trust_score');
      removeEncryptedToken('ripia_last_activity');
    } catch (error) {
      logger.error('全トークンクリアエラー:', error);
    }
  }

  // ========================
  // JWT Utilities
  // ========================

  /**
   * モックJWT生成（開発用）
   */
  public static generateMockJWT(user: Partial<User> | JWTPayload): string {
    const header = btoa(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));

    const payloadData =
      'sub' in user
        ? user
        : {
            sub: user.id,
            email: user.email,
            role: user.role,
            storeId: user.storeId,
            permissions: user.permissions || [],
            iat: Math.floor(Date.now() / 1000),
            exp: Math.floor(Date.now() / 1000) + 24 * 60 * 60,
          };

    const payload = btoa(JSON.stringify(payloadData));
    const signature = btoa(`mock-signature-${Date.now()}`);

    return `${header}.${payload}.${signature}`;
  }

  /**
   * JWTペイロード抽出
   */
  static extractJWTPayload(): EnhancedJWTPayload | null {
    try {
      const token = loadEncryptedToken(TOKEN_KEYS.ACCESS_TOKEN);
      if (!token) return null;

      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload as EnhancedJWTPayload;
    } catch (error) {
      logger.error('Failed to extract JWT payload:', error);
      return null;
    }
  }

  // ========================
  // Private Helpers
  // ========================

  /**
   * セキュリティレベル決定
   */
  private static determineSecurityLevel(
    requestedLevel: 'standard' | 'high' | 'premium' | undefined,
    deviceAuth: DeviceAuthResult
  ): 'standard' | 'high' | 'premium' {
    if (deviceAuth.trustScore < 30) {
      return 'premium';
    }

    if (deviceAuth.isNewDevice && !requestedLevel) {
      return 'high';
    }

    return requestedLevel || 'standard';
  }

  /**
   * エンタープライズトークン保存
   */
  private static saveEnhancedTokens(tokenData: {
    accessToken: string;
    refreshToken: string;
    user: User;
    jti: string;
    deviceFingerprint?: string;
    securityLevel: string;
  }): void {
    try {
      saveEncryptedToken(tokenData.accessToken, TOKEN_KEYS.ACCESS_TOKEN);
      saveEncryptedToken(tokenData.refreshToken, TOKEN_KEYS.REFRESH_TOKEN);
      saveEncryptedToken(JSON.stringify(tokenData.user), TOKEN_KEYS.USER_DATA);

      saveEncryptedToken(tokenData.jti, 'ripia_jwt_jti');
      saveEncryptedToken(tokenData.securityLevel, 'ripia_security_level');
      saveEncryptedToken(Date.now().toString(), 'ripia_last_activity');

      if (tokenData.deviceFingerprint) {
        saveEncryptedToken(
          tokenData.deviceFingerprint,
          'ripia_device_fingerprint'
        );
      }
    } catch (error) {
      logger.error('エンタープライズトークン保存エラー:', error);
      throw new AuthError(
        'セキュリティ情報の保存に失敗しました',
        'SERVER_ERROR'
      );
    }
  }
}

export default AuthenticationService;
